// ONE document-level listener that computes the accessible label once (innerText forces layout) and fans out to sinks.
// NEVER captures keystrokes or input values — only the clicked control's label.

const MAX_LABEL_LEN = 120

export const INTERACTIVE_SELECTOR =
  'button, a, select, [role="button"], [role="tab"], [role="menuitem"], [role="option"], [role="switch"], [role="checkbox"], input[type="checkbox"], input[type="radio"], input[type="submit"]'

/** The clicked control's accessible label — aria-label / title / visible text — never its value. */
export function elementLabel(el: Element): string {
  const aria = el.getAttribute('aria-label')?.trim()
  if (aria) return aria.slice(0, MAX_LABEL_LEN)
  const title = el.getAttribute('title')?.trim()
  if (title) return title.slice(0, MAX_LABEL_LEN)
  const rendered = (el as HTMLElement).innerText ?? el.textContent ?? ''
  const text = rendered.replace(/\s+/g, ' ').trim()
  return (text || el.tagName.toLowerCase()).slice(0, MAX_LABEL_LEN)
}

type ClickSink = (label: string) => void

const sinks = new Set<ClickSink>()
let listenerInstalled = false

// The shared listener is installed lazily on first registration; registering the same sink twice is a no-op.
export function registerClickSink(sink: ClickSink): void {
  if (typeof window === 'undefined') return
  sinks.add(sink)
  if (listenerInstalled) return
  listenerInstalled = true

  document.addEventListener(
    'click',
    (event) => {
      try {
        const target = event.target
        if (!(target instanceof Element)) return
        // A control hidden from the a11y tree is never the one the user believes they clicked, so resolve from its parent.
        // The iOS haptic switches (route-transition/haptics.ts) are laid invisibly over every section link, and without
        // this every iOS nav landed in the trail as a click on "input" instead of on "Jobs".
        const decorative = target.closest('[aria-hidden="true"]')
        const control = (decorative ? decorative.parentElement : target)?.closest(INTERACTIVE_SELECTOR)
        if (!control) return
        const label = elementLabel(control)
        for (const sinkFn of sinks) {
          try {
            sinkFn(label)
          } catch {
            // One sink failing must not starve the others — and never break a real click.
          }
        }
      } catch {
        // Never let telemetry break a real click.
      }
    },
    true,
  )
}
